import { DomainVO } from '../core/domain.vo';
import { UserApplicationVO } from './user-app.vo';
import { CountryVO } from './../locale/country.vo';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizActionObj, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { User, UserApplication } from '@wizeoo/akm-message-lib';
import { Model } from '../../model/model';
import { entity, field } from '@wizeoo/akm-compute-lib';


/** un utilisateur */
@entity('wizeoo.allkey.user.message.User', 'BU')
export class UserVO {
  
  /** Le VO source provenant du back  */
  protected _source: User;

  public isFake: boolean = false;
  /** Indique si le user a été chargé (dom) */
  public loaded: boolean = false;


  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }

  constructor(model: Model, pSource?: User) {
    this._model = model;
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new User();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  public set source(user: User) {
    this._source = user;
    // reset des collections volatiles
    this._userApps = null;
    this._userAppsByPerimeter = null;
    this._domains = null;

  }
  public get source(): User {
    return this._source;
  }

  public set uid(uid: string) {
    this._source.uid = uid;
  }
  public get uid(): string {
    return this._source.uid;
  }

  @field('string')
  public set mail(login: string) {
    this._source.mail = login;
  }

  public get mail(): string {
    return this._source.mail;
  }

  public set domain(domain: string) {
    this._source.domain = domain;
  }

  public get domain(): string {
    return this._source.domain;
  }

  public set businessUnitCode(businessUnitCode: string) {
    this._source.businessUnitCode = businessUnitCode;
  }

  public get businessUnitCode(): string {
    return this._source.businessUnitCode;
  }

  // Id technique
  @field('number')
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  @field('string')
  public set firstname(fname: string) {
    this._source.firstname = fname;
  }

  public get firstname(): string {
    return this._source.firstname;
  }

  @field('string')
  public set lastname(lname: string) {
    this._source.lastname = lname;
  }

  public get lastname(): string {
    return this._source.lastname;
  }

  @field('boolean')
  public set active(active: boolean) {
    this._source.active = active;
  }

  public get active(): boolean {
    return this._source.active;
  }

  @field('boolean')
  public set locked(locked: boolean) {
    this._source.locked = locked;
  }

  public get locked(): boolean {
    return this._source.locked;
  }

  /* ################################################
              composites
     ################################################ */

  /** collection de boutons favoris */
  public getFavoriteButtons(): WizActionObj[] {
    return null;
  }

  /** Pays du user pour sélection par défaut */
  public getDefaultCountry(): CountryVO {
    return this.model.countryMap.get('EN');
  }

  /** locale du user */
  public getLocale(): string {
    return WizTranslationManager.getInstance().dateLocale;
  }

  /**
   * l'objet existe en BDD (id)
   */
  public get exist(): boolean {
    return this.id > 0;
  }

  /**
   * getter de trsLabel pour pouvoir se servir du AgLabelValidityRenderer
   */
  public get trsLabel(): string {
    return this.lastname;
  }

  @field('UserApplication', true)
  public get userApplications(): UserApplication[] {
    if (!this._source.userApplications) {
      this._source.userApplications = [];
    }

    return this._source.userApplications;
  }
  public set userApplications(value: UserApplication[]) {
    this._source.userApplications = value;
  }

  
  @field('string')
  public get newPassword(): string {
    return this.source.newPassword;
  }
  public set newPassword(pValue: string) {
    this.source.newPassword = pValue;
  }

  /** Notification au user de la création de son mail */
  @field('boolean')
  public set notifyUser(value: boolean) {
    this.source.notifyUser = value;
  }
  public get notifyUser(): boolean {
    return this.source.notifyUser;
  }

  // TODO : détecter les changements sur les sous-collections
  @field('maintenanceFlag') // patch pour s'assurer de la demande de mise à jour sur contenu de steps
  public get maintenanceFlag(): string {
    return this.source.maintenanceFlag;
  }
  public set maintenanceFlag(value: string) {
    this.source.maintenanceFlag = value;
  }


  private _userApps: UserApplicationVO[]; // mode flat
  private _userAppsByPerimeter: Map<string, Map<string, Map<string, Map<string, UserApplicationVO>>>>; // fonctionalité par domain / bu / vision_code /  perimeter (dom / bu / app) = userAppVO

  /**
   * Accès aux applications de l'utilisateur.
   */
  public get userApplicationsVO(): UserApplicationVO[] {
    if (!this._userApps) {
      this._userApps = [];

      if (!!this.userApplications) {
        this.userApplications.forEach((ua: UserApplication) => {
          const uavo = new UserApplicationVO(this.model, ua);
          uavo.ownerId = this.id; // TODO on s'en fout
          this._userApps.push(uavo);
        });
      }
    }

    return this._userApps;
  }

  public get userAppsByPerimeter(): Map<string, Map<string, Map<string, Map<string, UserApplicationVO>>>> {   
    if (!this._userAppsByPerimeter) {
      this.model.userEngine.feedMapByPerimeter(this, null, this.userApplicationsVO);
    }
    return this._userAppsByPerimeter;
  }

  public set userAppsByPerimeter(map: Map<string, Map<string, Map<string, Map<string, UserApplicationVO>>>>) {   
    this._userAppsByPerimeter = map;
  }

  public get fullName(): string {
    return [this.firstname, this.lastname].join(' ');
  }

  protected _domains: DomainVO[];

  /**
   * Les domaines auxquels l'utilisateur est lié
   */
  public get domains(): DomainVO[] {
    if (!this._domains) {
      this._domains = [];

      if (!!this.source.domains) {
        this._domains = this.source.domains.map((d) => new DomainVO(this.model, d));
      }
    }

    return this._domains;
  }

  /** Tous les domaines auxquels le user a accès */
  public get allDomains(): DomainVO[] {
    let all: DomainVO[] = this.domains; // [...this.ownedDomains, ...this.domains]; // LMA patch... doublons
    const map: Map<string, DomainVO> = new Map(all.map(dom => [dom.code, dom]));
    return [...map.values()];
  }

  /**
   * collecte des restrictions sur les users par tags sur les apps
   * @returns 
   */
  public getVisionRestrictTags(): string[] {
    let result: string[] = null;

    // filtre direct pour la vision
    let app: UserApplicationVO = this.model.userEngine.getUserAppFromMap(this.model.connectedUser,
    this.model.domainCode, this.model.businessUnitCode, this.model.visionCode,
    'APP');
    
    if(!!app?.tags) {
      result = [];
      result.push(JSON.stringify(app.tags));
    } 
    
    // alimentation des tags depuis les profiles du user
    let user: UserVO = this.model.connectedUser;
    user.userApplicationsVO.forEach(app => {
      if(app.businessUnitCode === this.model.businessUnitCode && 
      app.visionCode === this.model.visionCode) {
        if(app.profiles?.length > 0) {
          app.profiles.forEach(prof => {
            if(!!prof?.tags) {
              if(!result) {
                result = [];
              }
              result.push(JSON.stringify(prof.tags));
            } 
          });
          
        }
      }
    });

    return result;
  }

}
